"use client";

import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import { ChevronLeft, ChevronRight, Github, ExternalLink, ArrowUpRight } from "lucide-react";
import Link from "next/link";
import { Project } from "@/types";

interface ProjectsSectionProps {
    projects: Project[];
}

export default function ProjectsSection({ projects }: ProjectsSectionProps) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);

    const projectsPerView = 3;
    const totalSlides = Math.ceil(projects.length / projectsPerView);
    const canGoNext = currentIndex < totalSlides - 1;
    const canGoPrev = currentIndex > 0;

    const nextSlide = () => {
        if (canGoNext) {
            setCurrentIndex((prev) => prev + 1);
        }
    };

    const prevSlide = () => {
        if (canGoPrev) {
            setCurrentIndex((prev) => prev - 1);
        }
    };

    const visibleProjects = projects.slice(
        currentIndex * projectsPerView,
        (currentIndex + 1) * projectsPerView
    );

    return (
        <section
            id="projects"
            className="section-snap flex items-center bg-surface min-h-screen py-20"
        >
            <div className="container mx-auto px-section-x">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="mb-16"
                >
                    <h2
                        className="text-section-title font-bold text-foreground text-center mb-4"
                        style={{ fontFamily: "var(--font-playfair), Georgia, serif" }}
                    >
                        Featured Projects
                    </h2>
                    <p className="text-body text-text-secondary mt-4 text-center">
                        A selection of my recent work
                    </p>
                </motion.div>

                {/* Projects Carousel */}
                <div ref={containerRef} className="relative">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentIndex}
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -50 }}
                            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                        >
                            {visibleProjects.map((project, index) => (
                                <ProjectCard
                                    key={project.id}
                                    project={project}
                                    index={index}
                                />
                            ))}
                        </motion.div>
                    </AnimatePresence>

                    {/* Navigation Arrows */}
                    {projects.length > projectsPerView && (
                        <div className="flex justify-end gap-3 mt-12">
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={prevSlide}
                                disabled={!canGoPrev}
                                aria-label="Previous projects"
                                className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 border-2 ${canGoPrev
                                    ? "border-accent text-accent hover:bg-accent hover:text-white"
                                    : "border-border text-border cursor-not-allowed"
                                    }`}
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </motion.button>
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={nextSlide}
                                disabled={!canGoNext}
                                aria-label="Next projects"
                                className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 border-2 ${canGoNext
                                    ? "border-accent text-accent hover:bg-accent hover:text-white"
                                    : "border-border text-border cursor-not-allowed"
                                    }`}
                            >
                                <ChevronRight className="w-5 h-5" />
                            </motion.button>
                        </div>
                    )}

                    {/* Slide Indicators */}
                    {totalSlides > 1 && (
                        <div className="flex justify-center gap-2 mt-6">
                            {Array.from({ length: totalSlides }).map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setCurrentIndex(i)}
                                    aria-label={`Go to slide ${i + 1}`}
                                    className={`w-2 h-2 rounded-full transition-all duration-300 ${i === currentIndex
                                        ? "bg-accent w-6"
                                        : "bg-border hover:bg-text-secondary/40"
                                        }`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

interface ProjectCardProps {
    project: Project;
    index: number;
}

function ProjectCard({ project, index }: ProjectCardProps) {
    return (
        <motion.article
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="group relative w-full aspect-[3/4] rounded-[2.5rem] overflow-hidden bg-surface-elevated cursor-pointer"
        >
            <div className="block w-full h-full">
                <Link
                    href={`/projects/${project.slug}`}
                    className="absolute inset-0 z-10"
                    aria-label={`View ${project.title} details`}
                />
                {/* Background Image */}
                <div className="absolute inset-0 w-full h-full">
                    {project.thumbnail ? (
                        <Image
                            src={project.thumbnail}
                            alt={`${project.title} preview`}
                            fill
                            className="object-cover transition-transform duration-700 ease-out group-hover:scale-110 group-focus:scale-110 group-active:scale-110"
                            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px"
                            priority={index < 3}
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-surface to-surface-elevated">
                            <span className="text-4xl font-bold opacity-10">
                                {project.title.charAt(0)}
                            </span>
                        </div>
                    )}
                </div>

                {/* Content Overlay */}
                <div className="absolute inset-0 transition-all duration-500 p-8 flex flex-col justify-between pointer-events-none z-10 group-hover:bg-black/60 group-focus:bg-black/60 group-active:bg-black/60">
                    {/* Contrast Protection for normal state - subtle localized vignettes */}
                    <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/30 to-transparent opacity-100 group-hover:opacity-0 group-focus:opacity-0 group-active:opacity-0 transition-opacity duration-500" />
                    <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/40 to-transparent opacity-100 group-hover:opacity-0 group-focus:opacity-0 group-active:opacity-0 transition-opacity duration-500" />

                    {/* Top Content */}
                    <div>
                        <h3 className="text-3xl font-bold text-white mb-2 drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)] relative z-20">
                            {project.title}
                        </h3>
                        {/* Description - revealed on hover */}
                        <p className="text-white/90 text-sm leading-relaxed line-clamp-3 opacity-0 group-hover:opacity-100 group-focus:opacity-100 group-active:opacity-100 transform translate-y-4 group-hover:translate-y-0 group-focus:translate-y-0 group-active:translate-y-0 transition-all duration-500 delay-75 ease-out">
                            {project.shortDescription}
                        </p>
                    </div>

                    {/* Bottom Content */}
                    <div className="relative">
                        {/* Tech Stack Tags - Always Visible */}
                        {project.techStack && project.techStack.length > 0 && (
                            <div className="flex flex-wrap gap-2 transform transition-transform duration-500 ease-out group-hover:-translate-y-16 group-focus:-translate-y-16 group-active:-translate-y-16 relative z-20 drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]">
                                {project.techStack.slice(0, 3).map((tech, idx) => (
                                    <span
                                        key={idx}
                                        className="px-4 py-1.5 rounded-full bg-white/20 text-white text-xs font-medium backdrop-blur-md border border-white/10"
                                    >
                                        {typeof tech === 'string' ? tech : tech.label}
                                    </span>
                                ))}
                                {project.techStack.length > 3 && (
                                    <span className="px-3 py-1.5 rounded-full bg-white/10 text-white text-xs font-medium backdrop-blur-md border border-white/10">
                                        +{project.techStack.length - 3}
                                    </span>
                                )}
                            </div>
                        )}

                        <div className="absolute inset-x-0 bottom-0 flex items-center justify-between">
                            {/* Buttons - revealed on hover */}
                            <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 group-focus:opacity-100 group-active:opacity-100 transform translate-y-4 group-hover:translate-y-0 group-focus:translate-y-0 group-active:translate-y-0 transition-all duration-300 delay-75 ease-out">
                                <div
                                    className="flex items-center gap-2 px-5 py-2.5 bg-white text-black rounded-full text-sm font-semibold hover:bg-accent hover:text-white hover:scale-105 active:scale-95 transition-all duration-300 shadow-sm"
                                >
                                    View Details
                                </div>
                                {project.github && (
                                    <a
                                        href={project.github}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="p-2.5 bg-white/10 hover:bg-white/20 text-white rounded-full backdrop-blur-md border border-white/10 transition-all active:scale-95 pointer-events-auto relative z-20"
                                        title="View Source on GitHub"
                                    >
                                        <Github className="w-5 h-5" />
                                    </a>
                                )}
                            </div>

                            {/* Arrow Indicator - Always Visible */}
                            <div className="p-3 bg-white/10 rounded-2xl backdrop-blur-md border border-white/10 group-hover:rotate-45 group-focus:rotate-45 group-active:rotate-45 transition-transform duration-500 flex-shrink-0 relative z-20">
                                <ArrowUpRight className="w-6 h-6 text-white" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </motion.article>
    );
}
